import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications } from '../../hooks/useNotifications';
import { 
  RESPONSE_TYPES, 
  RESPONSE_CONFIG 
} from '../../constants/notificationConstants';
import Button from '../ui/Button';

const NotificationResponse = ({ notificationId, currentResponse, compact = false, onResponseUpdate }) => {
  const { isMobile } = useTheme();
  const { respondToNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(currentResponse);
  const [error, setError] = useState(null);

  const handleResponse = async (response) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Responding to notification:', notificationId, 'with response:', response);
      
      // Update local state immediately for instant UI feedback
      setSelectedResponse(response);
      
      // Call the API
      await respondToNotification(notificationId, response);
      
      // Notify parent component of the update
      if (onResponseUpdate) {
        onResponseUpdate(notificationId, response);
      }
      
      console.log('Response updated successfully:', response);
      
    } catch (error) {
      console.error('Failed to respond:', error);
      setError('Failed to update response. Please try again.');
      
      // Revert local state on error
      setSelectedResponse(currentResponse);
    } finally {
      setLoading(false);
    }
  };

  // Don't show if already responded (unless pending)
  if (selectedResponse && selectedResponse !== RESPONSE_TYPES.PENDING) {
    return (
      <div className="text-center">
        <div className={`inline-flex items-center px-4 py-2 rounded-lg ${RESPONSE_CONFIG[selectedResponse].color}`}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          You responded: {RESPONSE_CONFIG[selectedResponse].label}
        </div>
        {error && (
          <div className="mt-2 text-sm text-error">
            {error}
          </div>
        )}
      </div>
    );
  }

  const responses = [
    {
      type: RESPONSE_TYPES.ACCEPT,
      label: 'Accept',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      variant: 'success'
    },
    {
      type: RESPONSE_TYPES.TENTATIVE,
      label: 'Tentative',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      variant: 'warning'
    },
    {
      type: RESPONSE_TYPES.REJECT,
      label: 'Reject',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      ),
      variant: 'error'
    }
  ];

  return (
    <div className="space-y-3">
      <div className="text-center">
        <p className="text-sm font-medium text-text-primary mb-3">
          Please respond to this {compact ? 'invitation' : 'invitation/meeting'}:
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="text-center">
          <div className="text-sm text-error bg-error-subtle px-3 py-2 rounded-lg">
            {error}
          </div>
        </div>
      )}

      <div className={`flex ${compact || isMobile ? 'flex-col space-y-2' : 'flex-row justify-center space-x-3'}`}>
        {responses.map((response) => (
          <Button
            key={response.type}
            variant={response.variant}
            size={compact ? 'sm' : 'md'}
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              handleResponse(response.type);
            }}
            disabled={loading}
            loading={loading && selectedResponse === response.type}
            className={`flex items-center justify-center space-x-2 ${
              compact || isMobile ? 'w-full' : 'min-w-[100px]'
            }`}
          >
            {response.icon}
            <span>{response.label}</span>
          </Button>
        ))}
      </div>

      {compact && (
        <p className="text-xs text-text-tertiary text-center">
          Click to expand for more details
        </p>
      )}
    </div>
  );
};

export default NotificationResponse;